import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  FileText, Plus, Download, Save, ArrowLeft, Calendar, User, Car, 
  MapPin, Gauge, Fuel, PenTool, Camera, CheckSquare, Eye, X, 
  AlertTriangle, Upload
} from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;
const LOGO_URL = '/skalartech-logo.png';

const VehicleHandoverProtocol = ({ onBack }) => {
  const [view, setView] = useState('list'); // 'list', 'create', 'view'
  const [protocols, setProtocols] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    handover_date: new Date().toISOString().slice(0, 16),
    vehicle_id: '',
    handover_from_employee_id: '',
    handover_to_employee_id: '',
    mileage: '',
    handover_location: '',
    exterior_condition: '',
    exterior_condition_notes: '',
    interior_condition: '',
    interior_condition_notes: '',
    fuel_level: '',
    fuel_level_notes: '',
    handed_over_items: [],
    tire_type: '',
    rim_type: '',
    damages_present: null,
    damage_description: '',
    notes: ''
  });

  const [rulesAccepted, setRulesAccepted] = useState(false);
  const fromSigCanvas = useRef(null);
  const toSigCanvas = useRef(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [damagePhotos, setDamagePhotos] = useState([]);

  // Options for form fields
  const conditionOptions = [
    { value: 'gewaschen', label: 'Gewaschen' },
    { value: 'leicht_verschmutzt', label: 'Leicht verschmutzt' },
    { value: 'stark_verschmutzt', label: 'Stark verschmutzt' },
    { value: 'sonstiges', label: 'Sonstiges' }
  ];

  const fuelLevelOptions = [
    { value: 'leer', label: 'Leer' },
    { value: '1_4', label: '1/4' },
    { value: '1_2', label: '1/2' },
    { value: '3_4', label: '3/4' },
    { value: 'voll', label: 'Voll' },
    { value: 'sonstiges', label: 'Sonstiges' }
  ];

  const handoverItems = [
    'fahrzeugschluessel', 'zulassungsbescheinigung_teil1', 'serviceheft', 'betriebsanleitung',
    'tankkarte', 'servicekarte', 'fahrermappe', 'navigation', 'fernbedienung', 
    'radiocode_karte', 'warndreieck', 'verbandskasten', 'warnweste'
  ];

  const handoverItemLabels = {
    fahrzeugschluessel: 'Fahrzeugschlüssel',
    zulassungsbescheinigung_teil1: 'Zulassungsbescheinigung Teil 1',
    serviceheft: 'Serviceheft',
    betriebsanleitung: 'Betriebsanleitung',
    tankkarte: 'Tankkarte',
    servicekarte: 'Servicekarte',
    fahrermappe: 'Fahrermappe',
    navigation: 'Navigation',
    fernbedienung: 'Fernbedienung',
    radiocode_karte: 'Radiocode-Karte',
    warndreieck: 'Warndreieck',
    verbandskasten: 'Verbandskasten',
    warnweste: 'Warnweste'
  };

  const tireTypes = [
    { value: 'sommerreifen', label: 'Sommerreifen' },
    { value: 'winterreifen', label: 'Winterreifen' },
    { value: 'ganzjahresreifen', label: 'Ganzjahresreifen' },
    { value: 'reserverad', label: 'Reserverad' }
  ];

  const rimTypes = [
    { value: 'alufelge', label: 'Alufelge' },
    { value: 'stahlfelge', label: 'Stahlfelge' }
  ];

  useEffect(() => {
    if (view === 'list') {
      fetchProtocols();
    } else if (view === 'create') {
      fetchEmployees();
      fetchVehicles();
    }
  }, [view]);

  const fetchProtocols = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/handover-protocols`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter for vehicle protocols only
      setProtocols(response.data.filter(p => p.protocol_type === 'vehicle'));
    } catch (error) {
      console.error('Fehler beim Laden der Protokolle:', error);
      alert('Fehler beim Laden der Protokolle');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.filter(emp => emp.status === 'aktiv'));
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Fahrzeuge:', error);
    }
  };

  const handleItemSelection = (item, checked) => {
    if (checked) {
      setFormData({
        ...formData,
        handed_over_items: [...formData.handed_over_items, item]
      });
    } else {
      setFormData({
        ...formData,
        handed_over_items: formData.handed_over_items.filter(i => i !== item)
      });
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.vehicle_id) {
      alert('Bitte wählen Sie ein Fahrzeug aus');
      return;
    }
    if (!formData.handover_from_employee_id || !formData.handover_to_employee_id) {
      alert('Bitte wählen Sie beide Mitarbeiter aus (Übergeber und Übernehmer)');
      return;
    }
    if (!formData.mileage) {
      alert('Bitte geben Sie den Kilometerstand ein');
      return;
    }
    if (!rulesAccepted) {
      alert('Bitte akzeptieren Sie die Regeln durch Setzen des Häkchens');
      return;
    }
    if (!fromSigCanvas.current || fromSigCanvas.current.isEmpty() || !toSigCanvas.current || toSigCanvas.current.isEmpty()) {
      alert('Bitte fügen Sie beide Unterschriften hinzu');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      // Get signature data
      const fromSignature = fromSigCanvas.current.toDataURL();
      const toSignature = toSigCanvas.current.toDataURL();
      
      const protocolData = {
        protocol_type: "vehicle",
        handover_date: new Date(formData.handover_date).toISOString(),
        vehicle_id: formData.vehicle_id,
        handover_from_employee_id: formData.handover_from_employee_id,
        handover_to_employee_id: formData.handover_to_employee_id,
        mileage: parseInt(formData.mileage),
        handover_location: formData.handover_location,
        exterior_condition: formData.exterior_condition,
        exterior_condition_notes: formData.exterior_condition_notes,
        interior_condition: formData.interior_condition,
        interior_condition_notes: formData.interior_condition_notes,
        fuel_level: formData.fuel_level,
        fuel_level_notes: formData.fuel_level_notes,
        handed_over_items: formData.handed_over_items,
        tire_type: formData.tire_type,
        rim_type: formData.rim_type,
        damages_present: formData.damages_present,
        damage_description: formData.damage_description,
        damage_photos: damagePhotos,
        handover_from_signature: fromSignature,
        handover_to_signature: toSignature,
        signature_data: toSignature, // Use receiving employee's signature as main
        notes: formData.notes
      };

      await axios.post(`${BACKEND_URL}/api/handover-protocols`, protocolData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('KFZ-Übergabeprotokoll erfolgreich erstellt');
      setView('list');
      resetForm();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern des Protokolls');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      handover_date: new Date().toISOString().slice(0, 16),
      vehicle_id: '',
      handover_from_employee_id: '',
      handover_to_employee_id: '',
      mileage: '',
      handover_location: '',
      exterior_condition: '',
      exterior_condition_notes: '',
      interior_condition: '',
      interior_condition_notes: '',
      fuel_level: '',
      fuel_level_notes: '',
      handed_over_items: [],
      tire_type: '',
      rim_type: '',
      damages_present: null,
      damage_description: '',
      notes: ''
    });
    setRulesAccepted(false);
    setDamagePhotos([]);
    if (fromSigCanvas.current) fromSigCanvas.current.clear();
    if (toSigCanvas.current) toSigCanvas.current.clear();
  };

  const generatePDF = async (protocol) => {
    try {
      const pdf = new jsPDF();
      let yPos = 20;

      // Add logo
      try {
        pdf.addImage(LOGO_URL, 'PNG', 20, yPos, 40, 20);
        yPos += 30;
      } catch (e) {
        console.log('Logo konnte nicht geladen werden');
        yPos += 10;
      }
      
      // Header
      pdf.setFontSize(18);
      pdf.text('KFZ-ÜBERGABEPROTOKOLL', 20, yPos);
      yPos += 15;
      
      // Protocol info
      pdf.setFontSize(12);
      pdf.text(`Protokoll-Nr.: ${protocol.protocol_number}`, 20, yPos);
      yPos += 8;
      pdf.text(`Datum: ${new Date(protocol.handover_date).toLocaleDateString('de-DE')}`, 20, yPos);
      yPos += 8;
      pdf.text(`Ort: ${protocol.handover_location || 'Nicht angegeben'}`, 20, yPos);
      yPos += 15;

      // Vehicle info
      pdf.text('Fahrzeug:', 20, yPos);
      yPos += 8;
      if (protocol.vehicle_details) {
        pdf.text(`${protocol.vehicle_details.make} ${protocol.vehicle_details.model} (${protocol.vehicle_details.license_plate})`, 25, yPos);
        yPos += 8;
      }
      pdf.text(`Kilometerstand: ${protocol.mileage} km`, 25, yPos);
      yPos += 15;

      // Persons
      pdf.text('Personen:', 20, yPos);
      yPos += 8;
      pdf.text(`Übergeber: ${protocol.handover_from_employee_name}`, 25, yPos);
      yPos += 8;
      pdf.text(`Übernehmer: ${protocol.handover_to_employee_name}`, 25, yPos);
      yPos += 15;

      // Vehicle condition
      pdf.text('Fahrzeugzustand:', 20, yPos);
      yPos += 8;
      pdf.text(`Außen: ${protocol.exterior_condition || 'Nicht angegeben'}`, 25, yPos);
      if (protocol.exterior_condition_notes) {
        yPos += 6;
        pdf.text(`  Anmerkungen: ${protocol.exterior_condition_notes}`, 25, yPos);
      }
      yPos += 8;
      pdf.text(`Innenraum: ${protocol.interior_condition || 'Nicht angegeben'}`, 25, yPos);
      if (protocol.interior_condition_notes) {
        yPos += 6;
        pdf.text(`  Anmerkungen: ${protocol.interior_condition_notes}`, 25, yPos);
      }
      yPos += 8;
      pdf.text(`Tankfüllung: ${protocol.fuel_level || 'Nicht angegeben'}`, 25, yPos);
      yPos += 15;

      // Handed over items
      if (protocol.handed_over_items && protocol.handed_over_items.length > 0) {
        pdf.text('Übergebene Gegenstände:', 20, yPos);
        yPos += 8;
        protocol.handed_over_items.forEach(item => {
          pdf.text(`• ${handoverItemLabels[item] || item}`, 25, yPos);
          yPos += 6;
        });
        yPos += 10;
      }

      // Rules
      if (yPos > 250) {
        pdf.addPage();
        yPos = 20;
      }
      pdf.text('Regeln und Bestimmungen:', 20, yPos);
      yPos += 10;
      const rulesLines = pdf.splitTextToSize(protocol.confirmation_text, 170);
      pdf.text(rulesLines, 20, yPos);
      yPos += rulesLines.length * 5 + 15;

      // Signatures
      if (yPos > 220) {
        pdf.addPage();
        yPos = 20;
      }
      pdf.text('Unterschriften:', 20, yPos);
      yPos += 15;

      // From signature
      if (protocol.handover_from_signature) {
        pdf.text('Übergeber:', 20, yPos);
        try {
          pdf.addImage(protocol.handover_from_signature, 'PNG', 20, yPos + 5, 80, 30);
        } catch (e) {
          pdf.text('Unterschrift vorhanden', 20, yPos + 10);
        }
      }

      // To signature  
      if (protocol.handover_to_signature) {
        pdf.text('Übernehmer:', 110, yPos);
        try {
          pdf.addImage(protocol.handover_to_signature, 'PNG', 110, yPos + 5, 80, 30);
        } catch (e) {
          pdf.text('Unterschrift vorhanden', 110, yPos + 10);
        }
      }

      pdf.save(`KFZ-Übergabeprotokoll-${protocol.protocol_number}.pdf`);
    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      alert('Fehler beim PDF-Export');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // List View
  if (view === 'list') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button onClick={onBack} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Zurück zum Fuhrpark
          </Button>
          <Button 
            onClick={() => setView('create')}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4" />
            KFZ-Übergabeprotokoll erstellen
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="w-5 h-5" />
              KFZ-Übergabeprotokolle ({protocols.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Laden...</div>
            ) : protocols.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Noch keine KFZ-Protokolle erstellt
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Protokoll-Nr.</th>
                      <th className="text-left p-3">Datum</th>
                      <th className="text-left p-3">Fahrzeug</th>
                      <th className="text-left p-3">Von → An</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {protocols.map((protocol) => (
                      <tr key={protocol.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{protocol.protocol_number}</td>
                        <td className="p-3">{formatDate(protocol.handover_date)}</td>
                        <td className="p-3">
                          {protocol.vehicle_details ? 
                            `${protocol.vehicle_details.make} ${protocol.vehicle_details.model} (${protocol.vehicle_details.license_plate})` :
                            'Fahrzeug nicht gefunden'
                          }
                        </td>
                        <td className="p-3">
                          {protocol.handover_from_employee_name} → {protocol.handover_to_employee_name}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            protocol.status === 'active' ? 'bg-green-100 text-green-800' :
                            protocol.status === 'returned' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {protocol.status === 'active' ? 'Aktiv' :
                             protocol.status === 'returned' ? 'Zurückgegeben' : 'Storniert'}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedProtocol(protocol);
                                setView('view');
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => generatePDF(protocol)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Create View - This will be very long, let me continue in next part
  if (view === 'create') {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <Button onClick={() => setView('list')} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Liste
          </Button>
        </div>

        <Card>
          <CardHeader className="text-center">
            {/* Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src={LOGO_URL} 
                alt="SKALARTECH Logo" 
                className="h-16 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <CardTitle className="text-2xl font-bold">KFZ-ÜBERGABEPROTOKOLL</CardTitle>
            <p className="text-gray-600 mt-2">
              Automatisches Datum: {new Date().toLocaleDateString('de-DE')} {new Date().toLocaleTimeString('de-DE')}
            </p>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Basic Information */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Date and Time */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Datum & Uhrzeit
                </label>
                <Input
                  type="datetime-local"
                  value={formData.handover_date}
                  onChange={(e) => setFormData({...formData, handover_date: e.target.value})}
                  className="w-full"
                />
              </div>

              {/* Handover Location */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Ort der Übergabe
                </label>
                <Input
                  value={formData.handover_location}
                  onChange={(e) => setFormData({...formData, handover_location: e.target.value})}
                  placeholder="z.B. Hauptsitz SKALARTECH"
                />
              </div>
            </div>

            {/* Persons */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <User className="w-5 h-5" />
                Angaben zu den Personen
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Übergebender (wer übergibt)</label>
                  <select
                    value={formData.handover_from_employee_id}
                    onChange={(e) => setFormData({...formData, handover_from_employee_id: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Bitte wählen...</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.first_name} {employee.last_name} ({employee.employee_number})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Übernehmender (wer übernimmt)</label>
                  <select
                    value={formData.handover_to_employee_id}
                    onChange={(e) => setFormData({...formData, handover_to_employee_id: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Bitte wählen...</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.first_name} {employee.last_name} ({employee.employee_number})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Vehicle Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Car className="w-5 h-5" />
                Fahrzeugangaben
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Fahrzeugauswahl</label>
                  <select
                    value={formData.vehicle_id}
                    onChange={(e) => setFormData({...formData, vehicle_id: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Fahrzeug wählen...</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.make} {vehicle.model} - {vehicle.license_plate}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Gauge className="w-4 h-4 inline mr-2" />
                    Kilometerstand
                  </label>
                  <Input
                    type="number"
                    value={formData.mileage}
                    onChange={(e) => setFormData({...formData, mileage: e.target.value})}
                    placeholder="z.B. 125000"
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Condition */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Fahrzeugzustand</h3>
              
              {/* Exterior Condition */}
              <div>
                <label className="block text-sm font-medium mb-2">Außen-Zustand</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {conditionOptions.map((option) => (
                    <label key={option.value} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="exterior_condition"
                        value={option.value}
                        checked={formData.exterior_condition === option.value}
                        onChange={(e) => setFormData({...formData, exterior_condition: e.target.value})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
                {formData.exterior_condition === 'sonstiges' && (
                  <Input
                    placeholder="Bitte spezifizieren..."
                    value={formData.exterior_condition_notes}
                    onChange={(e) => setFormData({...formData, exterior_condition_notes: e.target.value})}
                    className="mt-2"
                  />
                )}
              </div>

              {/* Interior Condition */}
              <div>
                <label className="block text-sm font-medium mb-2">Innenraum-Zustand</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {conditionOptions.map((option) => (
                    <label key={option.value} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="interior_condition"
                        value={option.value}
                        checked={formData.interior_condition === option.value}
                        onChange={(e) => setFormData({...formData, interior_condition: e.target.value})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
                {formData.interior_condition === 'sonstiges' && (
                  <Input
                    placeholder="Bitte spezifizieren..."
                    value={formData.interior_condition_notes}
                    onChange={(e) => setFormData({...formData, interior_condition_notes: e.target.value})}
                    className="mt-2"
                  />
                )}
              </div>
            </div>

            {/* Fuel Level */}
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                <Fuel className="w-5 h-5" />
                Tankfüllung
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                {fuelLevelOptions.map((option) => (
                  <label key={option.value} className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="fuel_level"
                      value={option.value}
                      checked={formData.fuel_level === option.value}
                      onChange={(e) => setFormData({...formData, fuel_level: e.target.value})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
              {formData.fuel_level === 'sonstiges' && (
                <Input
                  placeholder="Bitte spezifizieren..."
                  value={formData.fuel_level_notes}
                  onChange={(e) => setFormData({...formData, fuel_level_notes: e.target.value})}
                  className="mt-2"
                />
              )}
            </div>

            {/* Handed Over Items */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Übergebene Gegenstände</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {handoverItems.map((item) => (
                  <label key={item} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.handed_over_items.includes(item)}
                      onChange={(e) => handleItemSelection(item, e.target.checked)}
                      className="w-4 h-4 text-emerald-600 rounded"
                    />
                    <span className="text-sm">{handoverItemLabels[item]}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Tires and Rims */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Reifen und Felgen</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Montierte Reifen</label>
                  <select
                    value={formData.tire_type}
                    onChange={(e) => setFormData({...formData, tire_type: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Bitte wählen...</option>
                    {tireTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Felgenart</label>
                  <select
                    value={formData.rim_type}
                    onChange={(e) => setFormData({...formData, rim_type: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Bitte wählen...</option>
                    {rimTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Damages */}
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5" />
                Beschädigungen
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Sind Beschädigungen vorhanden?</label>
                  <div className="flex gap-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="damages_present"
                        value="true"
                        checked={formData.damages_present === true}
                        onChange={(e) => setFormData({...formData, damages_present: true})}
                        className="w-4 h-4"
                      />
                      <span>Ja</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="damages_present"
                        value="false"
                        checked={formData.damages_present === false}
                        onChange={(e) => setFormData({...formData, damages_present: false})}
                        className="w-4 h-4"
                      />
                      <span>Nein</span>
                    </label>
                  </div>
                </div>

                {formData.damages_present && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Beschreibung der Beschädigungen</label>
                    <textarea
                      value={formData.damage_description}
                      onChange={(e) => setFormData({...formData, damage_description: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      rows="3"
                      placeholder="Bitte beschreiben Sie die Beschädigungen..."
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Rules (Required) */}
            <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
              <h3 className="text-lg font-semibold mb-4">Regeln (Pflichtfeld)</h3>
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={rulesAccepted}
                  onChange={(e) => setRulesAccepted(e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded mt-1"
                />
                <div className="text-sm">
                  <CheckSquare className="w-4 h-4 inline mr-2" />
                  <strong>Ich habe es gelesen und akzeptiert:</strong>
                  <ul className="mt-2 text-gray-700 space-y-1 list-disc ml-6">
                    <li>Das Auto darf nicht zu privaten Zwecken genutzt werden.</li>
                    <li>Im Schadensfall ist der Fahrer verpflichtet, 500 Euro Selbstbeteiligung zu zahlen.</li>
                    <li>Jedes Wochenende (Freitagabend) müssen 8 Außenfotos und 1 Tachofoto per App gesendet werden.</li>
                    <li>Der Fahrer muss im Schadensfall den Arbeitgeber informieren und das Unfallprotokoll ausfüllen, sonst trägt er die Kosten.</li>
                    <li>Einmal im Monat muss der Außendienstmitarbeiter ein Foto des Führerscheins hochladen.</li>
                  </ul>
                </div>
              </label>
            </div>

            {/* Signatures */}
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                <PenTool className="w-5 h-5" />
                Unterschriften
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {/* From Signature */}
                <div>
                  <label className="block text-sm font-medium mb-2">Unterschrift Übergeber</label>
                  <div className="border border-gray-300 rounded-md">
                    <SignatureCanvas
                      ref={fromSigCanvas}
                      canvasProps={{
                        width: 300,
                        height: 150,
                        className: 'signature-canvas'
                      }}
                    />
                    <div className="p-2 bg-gray-50 border-t flex justify-end">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => fromSigCanvas.current?.clear()}
                      >
                        Löschen
                      </Button>
                    </div>
                  </div>
                </div>

                {/* To Signature */}
                <div>
                  <label className="block text-sm font-medium mb-2">Unterschrift Übernehmer</label>
                  <div className="border border-gray-300 rounded-md">
                    <SignatureCanvas
                      ref={toSigCanvas}
                      canvasProps={{
                        width: 300,
                        height: 150,
                        className: 'signature-canvas'
                      }}
                    />
                    <div className="p-2 bg-gray-50 border-t flex justify-end">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => toSigCanvas.current?.clear()}
                      >
                        Löschen
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-2">Notizen (optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
                placeholder="Zusätzliche Bemerkungen..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Save className="w-4 h-4" />
                {loading ? 'Speichern...' : 'Speichern'}
              </Button>
              <Button 
                variant="outline"
                onClick={() => setView('list')}
                disabled={loading}
              >
                Abbrechen
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // View Protocol Detail
  if (view === 'view' && selectedProtocol) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <Button onClick={() => setView('list')} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Liste
          </Button>
          <div className="flex gap-2">
            <Button 
              onClick={() => generatePDF(selectedProtocol)}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              PDF Export
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src={LOGO_URL} 
                alt="SKALARTECH Logo" 
                className="h-16 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <CardTitle className="text-xl">KFZ-Übergabeprotokoll: {selectedProtocol.protocol_number}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <strong>Datum & Uhrzeit:</strong>
                <div>{formatDate(selectedProtocol.handover_date)}</div>
              </div>
              <div>
                <strong>Ort der Übergabe:</strong>
                <div>{selectedProtocol.handover_location || 'Nicht angegeben'}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <strong>Übergeber:</strong>
                <div>{selectedProtocol.handover_from_employee_name}</div>
              </div>
              <div>
                <strong>Übernehmer:</strong>
                <div>{selectedProtocol.handover_to_employee_name}</div>
              </div>
            </div>

            <div>
              <strong>Fahrzeug:</strong>
              <div className="mt-2">
                {selectedProtocol.vehicle_details ? (
                  <div>
                    {selectedProtocol.vehicle_details.make} {selectedProtocol.vehicle_details.model} 
                    ({selectedProtocol.vehicle_details.license_plate})
                  </div>
                ) : (
                  <div>Fahrzeugdaten nicht verfügbar</div>
                )}
                <div className="text-sm text-gray-600">
                  Kilometerstand: {selectedProtocol.mileage} km
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <strong>Außenzustand:</strong>
                <div>{selectedProtocol.exterior_condition || 'Nicht angegeben'}</div>
                {selectedProtocol.exterior_condition_notes && (
                  <div className="text-sm text-gray-600">{selectedProtocol.exterior_condition_notes}</div>
                )}
              </div>
              <div>
                <strong>Innenraum:</strong>
                <div>{selectedProtocol.interior_condition || 'Nicht angegeben'}</div>
                {selectedProtocol.interior_condition_notes && (
                  <div className="text-sm text-gray-600">{selectedProtocol.interior_condition_notes}</div>
                )}
              </div>
              <div>
                <strong>Tankfüllung:</strong>
                <div>{selectedProtocol.fuel_level || 'Nicht angegeben'}</div>
                {selectedProtocol.fuel_level_notes && (
                  <div className="text-sm text-gray-600">{selectedProtocol.fuel_level_notes}</div>
                )}
              </div>
            </div>

            {selectedProtocol.handed_over_items && selectedProtocol.handed_over_items.length > 0 && (
              <div>
                <strong>Übergebene Gegenstände ({selectedProtocol.handed_over_items.length}):</strong>
                <ul className="list-disc ml-6 mt-2">
                  {selectedProtocol.handed_over_items.map((item, index) => (
                    <li key={index}>{handoverItemLabels[item] || item}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <strong>Reifen:</strong>
                <div>{selectedProtocol.tire_type || 'Nicht angegeben'}</div>
              </div>
              <div>
                <strong>Felgen:</strong>
                <div>{selectedProtocol.rim_type || 'Nicht angegeben'}</div>
              </div>
            </div>

            {selectedProtocol.damages_present !== null && (
              <div>
                <strong>Beschädigungen:</strong>
                <div>{selectedProtocol.damages_present ? 'Ja' : 'Nein'}</div>
                {selectedProtocol.damage_description && (
                  <div className="mt-2 p-3 bg-gray-50 rounded border">
                    {selectedProtocol.damage_description}
                  </div>
                )}
              </div>
            )}

            <div>
              <strong>Regeln und Bestimmungen:</strong>
              <div className="mt-2 p-3 bg-gray-50 rounded border text-sm">
                {selectedProtocol.confirmation_text}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {selectedProtocol.handover_from_signature && (
                <div>
                  <strong>Unterschrift Übergeber:</strong>
                  <div className="mt-2 border rounded p-2 bg-white">
                    <img 
                      src={selectedProtocol.handover_from_signature} 
                      alt="Unterschrift Übergeber" 
                      className="max-w-full border"
                    />
                  </div>
                </div>
              )}

              {selectedProtocol.handover_to_signature && (
                <div>
                  <strong>Unterschrift Übernehmer:</strong>
                  <div className="mt-2 border rounded p-2 bg-white">
                    <img 
                      src={selectedProtocol.handover_to_signature} 
                      alt="Unterschrift Übernehmer" 
                      className="max-w-full border"
                    />
                  </div>
                </div>
              )}
            </div>

            {selectedProtocol.notes && (
              <div>
                <strong>Notizen:</strong>
                <div className="mt-2 p-3 bg-gray-50 rounded border">
                  {selectedProtocol.notes}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <strong>Status:</strong> {selectedProtocol.status === 'active' ? 'Aktiv' : selectedProtocol.status}
              </div>
              <div>
                <strong>Erstellt:</strong> {formatDate(selectedProtocol.created_at)}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default VehicleHandoverProtocol;